import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Pencil, Plus, Search, Tags, Trash2 } from 'lucide-react';
import { formatIqd, fromMillidinars, type DeleteProductResult, type ProductDto } from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import { ProductFormDialog } from './ProductFormDialog';
import { StockAdjustDialog } from './StockAdjustDialog';
import {
  EMPTY_PRODUCT_FILTERS,
  useProductCategories,
  useProducts,
  useTags,
  type ProductFilters,
} from './useCafe';

/**
 * Products (SPEC §6.3).
 *
 * The column list is fixed by the spec and must not be trimmed or reordered:
 * Image · Name · Type · Barcode · Cost · Price · Opening Balance ·
 * Product Actions · Edit · Delete. The header carries search by product name,
 * a Select Categories filter, a Select Tags filter and Add New Product.
 */
export function ProductsPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  /*
   * Hidden products are OUT of this list unless asked for.
   *
   * Deleting a product that has ever been sold deactivates it instead of
   * removing it, so past bills keep their Product Sales figures. The row used
   * to stay on this screen with a small grey "(inactive)" beside the name,
   * which reads as "the delete did nothing" — the owner reported exactly that.
   * Delete now visibly removes it, and "show hidden" is how you get it back.
   */
  const [filters, setFilters] = useState<ProductFilters>({
    ...EMPTY_PRODUCT_FILTERS,
    onlyActive: true,
  });
  const [searchDraft, setSearchDraft] = useState('');
  const [editing, setEditing] = useState<ProductDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [adjusting, setAdjusting] = useState<ProductDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Says which of the two things a delete did — see `handleDelete`. */
  const [notice, setNotice] = useState<string | null>(null);

  const { categories } = useProductCategories();
  const { tags } = useTags();
  const { data, loading, reload } = useProducts(filters);

  // Debounced: a barcode scanner types a whole code in milliseconds and would
  // otherwise fire a request per character.
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((current) => ({ ...current, search: searchDraft })),
      300,
    );
    return () => clearTimeout(timer);
  }, [searchDraft]);

  const manage = can('products.manage');
  const products = data?.products ?? [];

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';
  const cellClass = 'px-3 py-2 align-middle';

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('cafe.products.title')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t('cafe.products.searchPlaceholder')}
            className={`${controlClass} w-64 ps-8`}
          />
        </div>

        <FilterDisclosure
          badge={
            (filters.productCategoryId ? 1 : 0) +
            (filters.tagId ? 1 : 0) +
            (filters.lowStockOnly ? 1 : 0) +
            (filters.onlyActive ? 0 : 1)
          }
        >

        <select
          value={filters.productCategoryId ?? ''}
          onChange={(event) =>
            setFilters((current) => ({
              ...current,
              productCategoryId: event.target.value || null,
            }))
          }
          className={controlClass}
        >
          <option value="">{t('cafe.products.selectCategories')}</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <select
          value={filters.tagId ?? ''}
          onChange={(event) =>
            setFilters((current) => ({ ...current, tagId: event.target.value || null }))
          }
          className={controlClass}
        >
          <option value="">{t('cafe.products.selectTags')}</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-content-secondary">
          <input
            type="checkbox"
            checked={filters.lowStockOnly}
            onChange={(event) =>
              setFilters((current) => ({ ...current, lowStockOnly: event.target.checked }))
            }
            className="h-4 w-4 accent-[var(--brand-primary)]"
          />
          {t('cafe.products.lowStockOnly')}
          {data && data.lowStockCount > 0 ? (
            <span className="rounded-full bg-ending/20 px-2 py-0.5 text-xs text-ending">
              {data.lowStockCount}
            </span>
          ) : null}
        </label>

        <label className="flex items-center gap-2 text-sm text-content-secondary">
          <input
            type="checkbox"
            checked={!filters.onlyActive}
            onChange={(event) =>
              setFilters((current) => ({ ...current, onlyActive: !event.target.checked }))
            }
            className="h-4 w-4 accent-[var(--brand-primary)]"
          />
          {t('cafe.products.showHidden')}
        </label>

        <div className="flex-1" />

        <Link
          to="/cafe/taxonomy"
          className="inline-flex items-center gap-1 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          <Tags className="h-4 w-4" aria-hidden />
          {t('cafe.taxonomy.title')}
        </Link>

        <Link
          to="/cafe"
          className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          {t('cafe.backToMenu')}
        </Link>
        </FilterDisclosure>

        {manage ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('cafe.products.addNew')}
          </button>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {error ? (
          <p role="alert" className="mb-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        {notice ? (
          <p role="status" className="mb-3 text-sm text-content-secondary">
            {notice}
          </p>
        ) : null}

        {loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          <>
          {/* Phone: a card per product — picture, name, price, stock and the
              same three actions, without the ten-column sideways drag. */}
          <div className="space-y-2 pb-3 md:hidden">
            {products.map((product) => (
              <div key={product.id} className="rounded-card border border-subtle bg-raised p-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-control bg-input">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs text-content-muted">{product.name.slice(0, 2)}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-content">
                      {product.name}
                      {product.isActive ? null : (
                        <span className="ms-2 text-xs font-normal text-content-muted">
                          {t('cafe.products.hidden')}
                        </span>
                      )}
                    </p>
                    <p className="tabular text-xs text-content-muted">
                      {formatIqd(fromMillidinars(product.priceMd), { currencySymbol })}
                      {' · '}
                      <span className={product.isLowStock ? 'text-ending' : undefined}>
                        {t('cafe.products.stock')}: {product.stockQuantity}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {manage ? (
                      <>
                        <IconButton
                          label={t('cafe.products.adjustStock')}
                          onClick={() => setAdjusting(product)}
                        >
                          <Plus className="h-4 w-4" aria-hidden />
                        </IconButton>
                        <IconButton label={t('common.edit')} onClick={() => setEditing(product)}>
                          <Pencil className="h-4 w-4" aria-hidden />
                        </IconButton>
                        <IconButton
                          label={
                            product.isActive
                              ? t('common.delete')
                              : t('cafe.products.deletePermanently')
                          }
                          danger
                          onClick={() => void handleDelete(product)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </IconButton>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <table className="w-full border-collapse text-sm max-md:hidden">
            <thead>
              <tr className="text-start text-content-secondary">
                <th className={`${cellClass} text-start`}>{t('common.image')}</th>
                <th className={`${cellClass} text-start`}>{t('common.name')}</th>
                <th className={`${cellClass} text-start`}>{t('cafe.products.type')}</th>
                <th className={`${cellClass} text-start`}>{t('cafe.products.barcode')}</th>
                <th className={`${cellClass} text-end`}>{t('cafe.products.cost')}</th>
                <th className={`${cellClass} text-end`}>{t('cafe.products.price')}</th>
                <th className={`${cellClass} text-end`}>{t('cafe.products.openingBalance')}</th>
                <th className={`${cellClass} text-start`}>{t('cafe.products.productActions')}</th>
                <th className={`${cellClass} text-start`}>{t('common.edit')}</th>
                <th className={`${cellClass} text-start`}>{t('common.delete')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={product.id} className={index % 2 === 0 ? 'bg-row-even' : 'bg-row-odd'}>
                  <td className={cellClass}>
                    <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-control bg-input">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-xs text-content-muted">
                          {product.name.slice(0, 2)}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className={cellClass}>
                    <div className="text-content">{product.name}</div>
                    <div className="flex flex-wrap items-center gap-1 text-xs text-content-muted">
                      {product.productCategoryName ? (
                        <span>{product.productCategoryName}</span>
                      ) : null}
                      {product.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded-full bg-hover px-1.5 py-0.5 text-content-secondary"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {!product.isActive ? (
                        <span className="text-content-muted">({t('cafe.products.inactive')})</span>
                      ) : null}
                    </div>
                  </td>

                  <td className={cellClass}>{t(`cafe.products.types.${product.type}`)}</td>
                  <td className={`${cellClass} tabular`}>{product.barcode ?? '—'}</td>
                  <td className={`${cellClass} tabular text-end`}>
                    {formatIqd(fromMillidinars(product.costMd), { currencySymbol })}
                  </td>

                  <td className={`${cellClass} tabular text-end`}>
                    <div>{formatIqd(fromMillidinars(product.priceMd), { currencySymbol })}</div>
                    {/* SPEC §6.3 "margin from cost vs price" — the number an
                        owner actually manages the menu by. */}
                    {product.marginPercent === null ? null : (
                      <div
                        className={`text-xs ${
                          product.marginPercent < 0 ? 'text-danger' : 'text-content-muted'
                        }`}
                      >
                        {t('cafe.products.margin', {
                          percent: product.marginPercent.toFixed(1),
                        })}
                      </div>
                    )}
                  </td>

                  <td className={`${cellClass} tabular text-end`}>
                    <div className={product.isLowStock ? 'text-ending' : 'text-content'}>
                      {product.stockQuantity}
                    </div>
                    <div className="text-xs text-content-muted">
                      {t('cafe.products.opened', { count: product.openingBalance })}
                    </div>
                  </td>

                  <td className={cellClass}>
                    {manage ? (
                      <button
                        type="button"
                        onClick={() => setAdjusting(product)}
                        className="inline-flex items-center gap-1 rounded-full border border-subtle px-2.5 py-1 text-xs text-content-secondary hover:bg-hover"
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden />
                        {t('cafe.products.adjustStock')}
                      </button>
                    ) : (
                      <span className="text-xs text-content-muted">—</span>
                    )}
                  </td>

                  <td className={cellClass}>
                    <IconButton
                      label={t('common.edit')}
                      disabled={!manage}
                      onClick={() => setEditing(product)}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </td>

                  <td className={cellClass}>
                    {/*
                      A hidden product is already off the menu, so "delete"
                      here can only mean permanently — and it says so. Live
                      products keep the ordinary delete, which hides them if
                      they have ever been sold.
                    */}
                    <IconButton
                      label={
                        product.isActive ? t('common.delete') : t('cafe.products.deletePermanently')
                      }
                      disabled={!manage}
                      danger
                      onClick={() => void handleDelete(product)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {adding || editing ? (
        <ProductFormDialog
          product={editing}
          categories={categories}
          tags={tags}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => void reload()}
        />
      ) : null}

      {adjusting ? (
        <StockAdjustDialog
          product={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => void reload()}
        />
      ) : null}
    </div>
  );

  async function handleDelete(product: ProductDto): Promise<void> {
    /*
     * Two different actions behind one icon, and the confirmation says which.
     *
     * On a LIVE product this hides-or-deletes and the server picks; on a
     * product that is already hidden it deletes for good. The second is only
     * reachable from the "Show hidden" view, so the destructive path takes two
     * deliberate steps rather than one mis-click on the menu.
     */
    const permanent = !product.isActive;
    const question = permanent
      ? t('cafe.products.confirmDeletePermanently', { name: product.name })
      : t('cafe.products.confirmDelete', { name: product.name });
    if (!window.confirm(question)) return;

    setError(null);
    setNotice(null);
    try {
      const result = await api.delete<DeleteProductResult>(
        `/products/${product.id}${permanent ? '?permanent=true' : ''}`,
      );
      setNotice(
        result.outcome === 'hidden'
          ? // Deliberately NOT `count`: i18next treats that key as a plural
            // selector and goes looking for `_one`/`_other` variants.
            t('cafe.products.hiddenNotRemoved', {
              name: product.name,
              times: result.soldLineCount,
            })
          : t('cafe.products.deleted', { name: product.name }),
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('cafe.products.couldNotDelete'));
    }
  }
}

function IconButton({
  children,
  label,
  disabled,
  danger,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`grid h-8 w-8 place-items-center rounded-control border border-subtle transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        danger
          ? 'text-content-secondary hover:bg-hover hover:text-danger'
          : 'text-content-secondary hover:bg-hover hover:text-content'
      }`}
    >
      {children}
    </button>
  );
}
